import { ArrowLeftRight, BarChart3, Check, ChevronDown, Crown, GripVertical, Info, PencilRuler, Plus, RotateCcw, Save, Search, Shield, SlidersHorizontal, Sparkles, Swords, Trash2, Users, X } from 'lucide-react';
import { type CSSProperties, type FormEvent, type PointerEvent, useEffect, useRef, useState } from 'react';
import { ElementBadge } from '../components/ElementBadge';
import { FilterSelect } from '../components/FilterSelect';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { PlayerProfileModal } from '../components/PlayerProfileModal';
import { StatRadar, statLabels } from '../components/StatRadar';
import { formations, techniqueById } from '../data';
import { playerRating } from '../competition';
import { nextClubFixture } from '../careerCalendar';
import { tacticalRoleForFormationSlot, tacticalRolesForPosition } from '../engine/ai/TacticalRoleSystem';
import type { GameState } from '../gameState';
import { affinityLabel, resolveAffinityPlayer } from '../specialCompetitions';
import type { Player, PlayerStatKey, TacticalRole, Tactics } from '../types';

type SliderTactic = Exclude<keyof Tactics, 'mentality'>;

const tacticMeta: Record<SliderTactic, { label: string; low: string; high: string; help: string }> = {
  pressing: { label: 'Presión', low: 'Replegar', high: 'Asfixiar', help: 'Intensidad para recuperar la posesión.' },
  tempo: { label: 'Ritmo', low: 'Pausado', high: 'Vertical', help: 'Velocidad de circulación y progresión.' },
  width: { label: 'Anchura', low: 'Estrecha', high: 'Abierta', help: 'Separación horizontal de los jugadores.' },
  defensiveLine: { label: 'Línea defensiva', low: 'Bloque bajo', high: 'Línea alta', help: 'Altura del bloque cuando el rival tiene el balón.' },
  risk: { label: 'Riesgo', low: 'Asegurar', high: 'Atacar espacios', help: 'Número de apoyos que rompen su posición en ataque.' },
  techniqueAggression: { label: 'Supertécnicas', low: 'Reservar TP', high: 'Buscar impacto', help: 'Predisposición a gastar TP cuando existe una opción válida.' },
};

const mentalityPresets = [
  { id: 'CONSERVATIVE', label: 'Conservadora', value: 0.28, help: 'El bloque avanza menos y prioriza esperar una ocasión clara.' },
  { id: 'BALANCED', label: 'Equilibrada', value: 0.52, help: 'Mantiene una altura y una selección de tiro neutrales.' },
  { id: 'OFFENSIVE', label: 'Ofensiva', value: 0.78, help: 'Adelanta el bloque, favorece el tiro y suma una ruptura en campo rival.' },
] as const;

const playerStatOrder: readonly PlayerStatKey[] = ['kick', 'dribble', 'pass', 'defense', 'speed', 'power', 'control', 'stamina'];
const tacticalRoleLabels: Record<TacticalRole, string> = {
  CENTRAL: 'Central', LATERAL: 'Lateral', LIBERO: 'Líbero',
  ORGANIZER: 'Organizador', BALL_WINNER: 'Recuperador', INTERIOR: 'Interior', ATTACKING_MIDFIELDER: 'Mediapunta',
  FINISHER: 'Finalizador', WINGER: 'Extremo', SECOND_STRIKER: 'Segundo punta',
};
const tacticalRolePriorities: Record<TacticalRole, string> = {
  CENTRAL: 'Marca, intercepción y protección del bloque.',
  LATERAL: 'Amplitud, apoyos exteriores y recuperación.',
  LIBERO: 'Anticipación y salida limpia desde atrás.',
  ORGANIZER: 'Apoyos, circulación y pases de progresión.',
  BALL_WINNER: 'Presión, robo e intercepción en el medio.',
  INTERIOR: 'Llegadas, apoyos interiores y recuperación.',
  ATTACKING_MIDFIELDER: 'Último pase, conducción y llegada.',
  FINISHER: 'Desmarques, presencia en el área y disparo.',
  WINGER: 'Amplitud, uno contra uno y progresión por banda.',
  SECOND_STRIKER: 'Apoyo al punta, asociación y remate.',
};
const archetypeDescriptions: Record<string, string> = {
  Afinidad: 'Mejora las acciones con ventaja elemental y técnicas afines.',
  Brecha: 'Castiga al rival cuando domina claramente el atributo del duelo.',
  Contraataque: 'Acelera las acciones verticales tras recuperar la posesión.',
  'Juego Sucio': 'Potencia duelos y presión, a cambio de más riesgo de falta.',
  'Juego sucio': 'Potencia duelos y presión, a cambio de más riesgo de falta.',
  Justicia: 'Responde mejor ante la adversidad y rivales agresivos.',
  Tensión: 'Rinde mejor en acciones decisivas y finales apretados.',
};

const standardTactics: Tactics = {
  mentality: 0.56, pressing: 0.58, tempo: 0.54, width: 0.5,
  defensiveLine: 0.52, risk: 0.46, techniqueAggression: 0.55,
};

function roleBorderTone(role: string) {
  if (role === 'GK') return 'goalkeeper';
  if (['LB', 'CB', 'RB', 'LWB', 'RWB'].includes(role)) return 'defence';
  if (['LM', 'CM', 'RM', 'DM', 'AM'].includes(role)) return 'midfield';
  return 'attack';
}

export function TacticsPage({ game }: { game: GameState }) {
  const availableFormations = [...formations, ...game.save.customFormations];
  const affinityEvent = game.save.affinityGames;
  const affinityReady = Boolean(affinityEvent?.squadPlayerIds.length && affinityEvent.lineupPlayerIds.length === 11);
  const nextFixture = nextClubFixture(game.save);
  const [squadView, setSquadView] = useState<'club' | 'affinity'>(() => affinityReady && nextFixture?.competition === 'affinity_games' ? 'affinity' : 'club');
  const isAffinityView = squadView === 'affinity' && affinityReady;
  const activeFormationId = isAffinityView ? affinityEvent!.formationId : game.save.formationId;
  const formation = availableFormations.find((item) => item.id === activeFormationId) ?? formations[0];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [rolePickerOpen, setRolePickerOpen] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  // The formation catalogue is intentionally opt-in: only its own trigger
  // opens it, so interacting with the board never expands the left panel.
  const [formationOpen, setFormationOpen] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customBaseId, setCustomBaseId] = useState(activeFormationId);
  const [layoutEditing, setLayoutEditing] = useState(false);
  const [draftPositions, setDraftPositions] = useState<typeof formation.positions>([]);
  const [draggingSlot, setDraggingSlot] = useState<number | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changeTargetId, setChangeTargetId] = useState<string | null>(null);
  const [changeCandidateId, setChangeCandidateId] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [recruitQuery, setRecruitQuery] = useState('');
  const [recruitPosition, setRecruitPosition] = useState<'ALL' | Player['position']>('ALL');
  const [recruitElement, setRecruitElement] = useState<'ALL' | Player['element']>('ALL');
  const pitchRef = useRef<HTMLDivElement>(null);
  const savedTimer = useRef<number | null>(null);
  const affinityPlayers = affinityEvent?.squadPlayerIds.flatMap((id) => resolveAffinityPlayer(game.save, id) ?? []) ?? [];
  const activePlayers = isAffinityView ? affinityPlayers : game.ownedPlayers;
  const ownedById = new Map(activePlayers.map((player) => [player.id, player]));
  const activeLineup = isAffinityView ? affinityEvent!.lineupPlayerIds : game.save.lineup;
  const activeBenchIds = isAffinityView ? affinityEvent!.benchPlayerIds : game.save.benchPlayerIds;
  const selected = ownedById.get(activeLineup[selectedIndex]);
  const bench = activeBenchIds.map((id) => ownedById.get(id)).filter(Boolean) as Player[];
  const benchIds = new Set(activeBenchIds);
  const reserves = isAffinityView ? [] : game.ownedPlayers.filter((player) => !activeLineup.includes(player.id) && !benchIds.has(player.id));
  const availableCandidates = reserves;
  const visibleCandidates = availableCandidates.filter((player) => {
    const nameMatches = player.name.toLocaleLowerCase('es').includes(recruitQuery.trim().toLocaleLowerCase('es'));
    return nameMatches && (recruitPosition === 'ALL' || player.position === recruitPosition) && (recruitElement === 'ALL' || player.element === recruitElement);
  });
  const resolvedChangeTargetId = changeTargetId;
  const changeTarget = resolvedChangeTargetId ? ownedById.get(resolvedChangeTargetId) : undefined;
  const changeTargetIndex = resolvedChangeTargetId ? activeLineup.indexOf(resolvedChangeTargetId) : -1;
  const changeTargetRole = changeTargetIndex >= 0 ? formation.roles[changeTargetIndex] : 'BANQUILLO';
  const changeTargetArea = !changeTarget ? 'ONCE / BANQUILLO' : changeTargetIndex >= 0 ? 'ONCE TITULAR' : 'BANQUILLO';
  const changeCandidate = changeCandidateId ? ownedById.get(changeCandidateId) : undefined;
  const isCustomFormation = formation.id.startsWith('custom_');
  const layoutPositions = layoutEditing ? draftPositions : formation.positions;
  const automaticRole = selected ? tacticalRoleForFormationSlot(selected.position, formation.roles[selectedIndex]) : null;
  const activeRoleOverrides = isAffinityView ? affinityEvent!.tacticalRoleOverrides : game.save.tacticalRoleOverrides;
  const assignedRole = selected ? activeRoleOverrides[selected.id] ?? automaticRole : null;
  const hasManualRole = Boolean(selected && activeRoleOverrides[selected.id]);
  const availableTacticalRoles = selected ? tacticalRolesForPosition(selected.position) : [];
  const canOverrideRole = availableTacticalRoles.length > 0;
  const selectedTechniques = selected
    ? selected.techniques.map((id) => techniqueById.get(id)).filter(Boolean)
    : [];
  const mentalityPreset = getMentalityPreset(game.save.tactics.mentality);

  useEffect(() => () => {
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
  }, []);

  useEffect(() => {
    setRolePickerOpen(false);
  }, [selected?.id]);

  useEffect(() => {
    if (nextFixture?.competition === 'affinity_games' && affinityReady) setSquadView('affinity');
    else setSquadView('club');
  }, [affinityReady, nextFixture?.id]);

  const announceSaved = () => {
    setShowSaved(true);
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => {
      setShowSaved(false);
      savedTimer.current = null;
    }, 2400);
  };

  const drop = (index: number) => {
    if (draggedId) {
      if (isAffinityView) game.moveAffinityLineupPlayer(draggedId, index);
      else game.moveLineupPlayer(draggedId, index);
      announceSaved();
    }
    setDraggedId(null);
    setSelectedIndex(index);
  };

  const createFormation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    game.createCustomFormation(customName, customBaseId);
    setCustomName('');
    setCreatorOpen(false);
    setFormationOpen(true);
    announceSaved();
  };

  const beginLayoutEdit = () => {
    setDraftPositions(formation.positions.map(([x, y]) => [x, y] as [number, number]));
    setLayoutEditing(true);
  };

  const placeSlotFromPointer = (index: number, event: PointerEvent<HTMLElement>) => {
    if (!pitchRef.current) return;
    const bounds = pitchRef.current.getBoundingClientRect();
    const y = ((event.clientX - bounds.left) / bounds.width) * 100;
    const x = 100 - ((event.clientY - bounds.top) / bounds.height) * 100;
    setDraftPositions((current) => current.map((position, positionIndex) => positionIndex === index
      ? [Math.min(97, Math.max(3, x)), Math.min(96, Math.max(4, y))] as [number, number]
      : position));
  };

  const updateSelectedSlot = (event: PointerEvent<HTMLDivElement>) => {
    if (!layoutEditing) return;
    placeSlotFromPointer(selectedIndex, event);
  };

  const beginSlotDrag = (event: PointerEvent<HTMLButtonElement>, index: number) => {
    if (!layoutEditing) return;
    event.preventDefault();
    event.stopPropagation();
    setSelectedIndex(index);
    setDraggingSlot(index);
    event.currentTarget.setPointerCapture(event.pointerId);
    placeSlotFromPointer(index, event);
  };

  const continueSlotDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (!layoutEditing || draggingSlot === null) return;
    event.preventDefault();
    placeSlotFromPointer(draggingSlot, event);
  };

  const saveLayout = () => {
    game.updateCustomFormationLayout(formation.id, draftPositions);
    setLayoutEditing(false);
    setDraggingSlot(null);
    announceSaved();
  };

  const deleteFormation = (formationId: string) => {
    game.deleteCustomFormation(formationId);
    if (formationId === formation.id) {
      setLayoutEditing(false);
      setDraftPositions([]);
      setDraggingSlot(null);
    }
    announceSaved();
  };

  const resetTactics = () => {
    (Object.keys(standardTactics) as (keyof Tactics)[]).forEach((key) => game.setTactic(key, standardTactics[key]));
    announceSaved();
  };

  const makeLineupChange = () => {
    if (!changeCandidateId || !resolvedChangeTargetId) return;
    game.swapReservePlayer(changeCandidateId, resolvedChangeTargetId);
    if (changeTargetIndex >= 0) setSelectedIndex(changeTargetIndex);
    setChangeTargetId(null);
    setChangeCandidateId(null);
    announceSaved();
  };

  const selectFormation = (formationId: string) => {
    if (isAffinityView) game.setAffinityFormation(formationId);
    else game.setFormation(formationId);
    announceSaved();
  };

  const closeChanges = () => {
    setChangesOpen(false);
    setChangeTargetId(null);
    setChangeCandidateId(null);
  };

  return (
    <div className="page tactics-page">
      <header className="page-heading compact-heading tactics-heading">
        <div><span className="eyebrow">PLAN DE PARTIDO</span><h1>Tácticas</h1><p>Construye el equipo arrastrando jugadores directamente sobre la pizarra.</p></div>
        {affinityReady && <div className="tactical-squad-tabs" aria-label="Registro de plantilla">
          <button className={!isAffinityView ? 'active' : ''} onClick={() => setSquadView('club')}>Club</button>
          <button className={isAffinityView ? 'active' : ''} onClick={() => setSquadView('affinity')}>Affinity</button>
        </div>}
        {showSaved && <div className="saved-indicator is-visible"><Check size={15} /> Cambios guardados</div>}
      </header>

      <div className="tactics-workspace">
        <aside className="tactics-left card">
          <div className="aside-title"><span className="eyebrow">SISTEMA</span><small>{availableFormations.length} formaciones</small></div>
          <button className={`formation-select ${formationOpen ? 'open' : ''}`} onClick={() => setFormationOpen((open) => !open)} aria-expanded={formationOpen}><span><small>FORMACIÓN ACTUAL</small><b>{formation.name}</b></span><ChevronDown size={17} /></button>
          {formationOpen && <div className="formation-options">
            {availableFormations.map((item) => <div className="formation-option" key={item.id}>
              <button className={item.id === formation.id ? 'active' : ''} onClick={() => selectFormation(item.id)}>
                <span className="formation-mini">{item.positions.map(([x, y], index) => <i style={{ left: `${y}%`, top: `${100 - x}%` }} key={index} />)}</span>
                <span><b>{item.name}</b><small>{item.id === formation.id ? 'En uso' : item.id.startsWith('custom_') ? 'Personalizada' : 'Seleccionar'}</small></span>{item.id === formation.id && <Check size={17} />}
              </button>
              {!isAffinityView && item.id.startsWith('custom_') && <button className="delete-formation-button" onClick={() => deleteFormation(item.id)} title={`Eliminar ${item.name}`} aria-label={`Eliminar ${item.name}`}><Trash2 size={15} /></button>}
            </div>)}
            {!isAffinityView && <button className="new-formation-button" onClick={() => { setCreatorOpen((open) => !open); setCustomBaseId(formation.id); }}><Plus size={17} /><span><b>Nueva formación</b><small>Crear una variante guardada</small></span></button>}
          </div>
          }
          {creatorOpen && !isAffinityView && <form className="custom-formation-builder" onSubmit={createFormation}>
            <span className="eyebrow">NUEVA VARIANTE</span>
            <input value={customName} onChange={(event) => setCustomName(event.target.value)} maxLength={32} placeholder={`Variante ${formation.name}`} />
            <select value={customBaseId} onChange={(event) => setCustomBaseId(event.target.value)}>{availableFormations.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}</select>
            <p>Se guardará una copia personalizada de esta estructura para tu partida.</p>
            <div><button type="button" onClick={() => setCreatorOpen(false)}>Cancelar</button><button type="submit"><Plus size={14} /> Guardar</button></div>
          </form>}

          <div className="divider" />
          <div className="aside-title"><span className="eyebrow">SUPLENTES</span><small>{bench.length}</small></div>
          <div className="bench-list">
            {bench.length ? bench.map((player) => <div className="bench-player" draggable onDragStart={() => setDraggedId(player.id)} onDragEnd={() => setDraggedId(null)} key={player.id}><GripVertical size={14} /><PlayerAvatar player={player} size="sm" /><span><b>{player.name}</b><small>{player.position} · {player.element}</small></span></div>) : <div className="bench-empty"><Users size={19} /><span><b>Sin suplentes</b><small>Recluta jugadores para ampliar el banquillo.</small></span></div>}
          </div>
        </aside>

        <main className="tactical-center card">
          <div className="board-toolbar"><span><Shield size={18} /> {isAffinityView ? `SELECCIÓN ${affinityLabel(affinityEvent!.element).toUpperCase()}` : game.save.clubName.toUpperCase()}</span><span className="board-toolbar-actions">{!isAffinityView && <button className="lineup-change-trigger" onClick={() => { setChangeTargetId(activeLineup[selectedIndex]); setChangeCandidateId(null); setRecruitQuery(''); setChangesOpen(true); }}><ArrowLeftRight size={15} /> Cambios de jugadores</button>}{isCustomFormation && !isAffinityView ? <span className="layout-actions">{layoutEditing ? <><button onClick={() => { setLayoutEditing(false); setDraftPositions([]); setDraggingSlot(null); }}><X size={14} /> Cancelar</button><button className="layout-save" onClick={saveLayout}><Save size={14} /> Guardar diseño</button></> : <button onClick={beginLayoutEdit}><PencilRuler size={14} /> Editar disposición</button>}</span> : <span className="drag-hint"><Info size={16} /> Arrastra para intercambiar</span>}</span></div>
          {layoutEditing && <div className="layout-edit-hint"><PencilRuler size={16} /><span><b>Editando {formation.name}</b><small>Arrastra un jugador para afinar su posición o pulsa sobre el campo para recolocarlo.</small></span></div>}
          <div className={`tactical-pitch ${layoutEditing ? 'layout-editing' : ''} ${draggedId ? 'drag-active' : ''}`} ref={pitchRef} onPointerDown={updateSelectedSlot} onPointerMove={continueSlotDrag} onPointerUp={() => setDraggingSlot(null)} onPointerCancel={() => setDraggingSlot(null)}>
            <div className="pitch-lines"><span className="center-line" /><span className="center-circle" /><span className="penalty top" /><span className="penalty bottom" /></div>
            {layoutPositions.map(([x, y], index) => {
              const player = ownedById.get(activeLineup[index]);
              if (!player) return null;
              return <button
                className={`pitch-player ${selectedIndex === index ? 'selected' : ''} ${draggingSlot === index ? 'position-dragging' : ''}`}
                style={{ left: `${y}%`, top: `${100 - x}%` }}
                draggable={!layoutEditing}
                onDragStart={() => !layoutEditing && setDraggedId(player.id)}
                onDragEnd={() => setDraggedId(null)}
                onDragOver={(event) => !layoutEditing && event.preventDefault()}
                onDrop={() => !layoutEditing && drop(index)}
                onPointerDown={(event) => layoutEditing ? beginSlotDrag(event, index) : setSelectedIndex(index)}
                onClick={() => setSelectedIndex(index)}
                key={`${index}-${player.id}`}
              >
                <span className={`pitch-portrait role-ring--${roleBorderTone(formation.roles[index])}`}><PlayerAvatar player={player} size="md" /><b>{index + 1}</b>{!isAffinityView && game.save.captainPlayerId === player.id && <em className="captain-marker" aria-label="Capitán">C</em>}<i /></span>
                <span className="pitch-name">{player.name.split(' ')[0]}</span>
                <small>{formation.roles[index]}</small>
              </button>;
            })}
          </div>
          {selected && <section className="selected-player-card">
            <div className="selected-strip">
              <div className="selected-player-main"><PlayerAvatar player={selected} size="md" /><span><small>PUESTO {selectedIndex + 1} · {formation.roles[selectedIndex]} · NATURAL {selected.position}</small><b>{selected.name}</b><em>{selected.metadata?.team ?? game.save.clubName}</em></span></div>
              {!isAffinityView && <button className={`captain-control ${game.save.captainPlayerId === selected.id ? 'is-captain' : ''}`} onClick={() => { game.setCaptain(selected.id); announceSaved(); }}><Crown size={16} /><span><small>CAPITÁN</small><b>{game.save.captainPlayerId === selected.id ? 'Asignado' : 'Asignar'}</b></span></button>}
              <span className="strip-affinity"><small>AFINIDAD</small><ElementBadge element={selected.element} /></span>
              <span className="strip-stat"><small>MEDIA</small><b>{average(selected)}</b></span>
              <span className="strip-stat"><small>TP</small><b>{selected.tp_max}</b></span>
              <button className="selected-profile-button" onClick={() => setProfileOpen(true)}><BarChart3 size={15} /><span><small>EXPEDIENTE</small><b>Ver ficha</b></span></button>
            </div>
            <div className="selected-player-details">
              <div className="selected-player-context">
                <article className="selected-role-context">
                  <small>ROL EN EL SISTEMA</small>
                  <b>{assignedRole ? tacticalRoleLabels[assignedRole] : 'Portero'}</b>
                  <p>{assignedRole ? tacticalRolePriorities[assignedRole] : 'Protege el área, prioriza la colocación y solo sale cuando el balón queda a su alcance.'}</p>
                  {canOverrideRole && assignedRole && <div className="role-override-picker"><button type="button" className={`role-override-trigger ${hasManualRole ? 'is-manual' : ''}`} onClick={() => setRolePickerOpen((open) => !open)} aria-expanded={rolePickerOpen} aria-haspopup="listbox" aria-controls="tactical-role-options"><PencilRuler size={12} /> {hasManualRole ? 'Editar rol' : 'Cambiar rol'}</button>
                    {rolePickerOpen && <div className="role-override-options" id="tactical-role-options" role="listbox" aria-label={`Rol táctico de ${selected.name}`}>
                      <span>PRIORIDADES DE IA</span>
                      {availableTacticalRoles.map((role) => <button type="button" role="option" aria-selected={role === assignedRole} className={role === assignedRole ? 'active' : ''} onClick={() => { if (isAffinityView) game.setAffinityTacticalRole(selected.id, role); else game.setTacticalRole(selected.id, role); setRolePickerOpen(false); announceSaved(); }} key={role}><b>{tacticalRoleLabels[role]}</b><small>{tacticalRolePriorities[role]}</small></button>)}
                      <button type="button" className="role-automatic-option" onClick={() => { if (isAffinityView) game.setAffinityTacticalRole(selected.id, null); else game.setTacticalRole(selected.id, null); setRolePickerOpen(false); announceSaved(); }}><b>Automático</b><small>Usar {automaticRole ? tacticalRoleLabels[automaticRole] : 'el rol de portero'} según el puesto.</small></button>
                    </div>}
                  </div>}
                </article>
                <article><small>ARQUETIPO · PASIVA</small><b>{selected.metadata?.archetype ?? 'Sin arquetipo'}</b><p>{archetypeDescriptions[selected.metadata?.archetype ?? ''] ?? 'Sin efecto pasivo registrado.'}</p></article>
              </div>
              <div className="selected-stat-grid" aria-label={`Estadísticas de ${selected.name}`}>
                {playerStatOrder.map((key) => <span key={key}><small>{statLabels[key] ?? key}</small><b>{selected.stats[key] ?? 0}</b></span>)}
              </div>
              <div className="selected-technique-list"><span><Swords size={13} /><small>ARSENAL ACTUAL</small></span><div>{selectedTechniques.length ? selectedTechniques.slice(0, 3).map((technique) => technique && <b key={technique.id}>{technique.name}</b>) : <em>Sin técnicas asignadas</em>}{selectedTechniques.length > 3 && <i>+{selectedTechniques.length - 3}</i>}</div></div>
            </div>
          </section>}
        </main>

        <aside className="tactics-right card">
          <div className="aside-title"><div><span className="eyebrow">INSTRUCCIONES</span><h2>Identidad de juego</h2></div><button title="Restablecer" onClick={resetTactics}><RotateCcw size={16} /></button></div>
          <p className="aside-copy">Los ajustes afectan directamente a las decisiones del simulador.</p>
          <div className="tactic-controls">
            <fieldset className="mentality-control">
              <legend><b>Mentalidad</b><span>{mentalityPreset.label}</span></legend>
              <div>{mentalityPresets.map((preset) => <button type="button" className={preset.id === mentalityPreset.id ? 'active' : ''} aria-pressed={preset.id === mentalityPreset.id} onClick={() => { game.setTactic('mentality', preset.value); announceSaved(); }} key={preset.id}>{preset.label}</button>)}</div>
              <p>{mentalityPreset.help}</p>
            </fieldset>
            {(Object.keys(tacticMeta) as SliderTactic[]).map((key) => {
              const meta = tacticMeta[key];
              const value = game.save.tactics[key];
              return <label className="tactic-control" key={key}>
                <span><b>{meta.label}</b><output>{Math.round(value * 100)}</output></span>
                <input type="range" min="0" max="1" step="0.01" value={value} style={{ '--p': `${value * 100}%` } as CSSProperties} aria-label={meta.label} onChange={(event) => { game.setTactic(key, Number(event.target.value)); announceSaved(); }} />
                <span className="range-labels"><small>{meta.low}</small><small>{meta.high}</small></span>
              </label>;
            })}
          </div>
          <div className="tactical-summary"><Sparkles size={17} /><div><small>LECTURA DEL SISTEMA</small><b>{getTacticSummary(game.save.tactics)}</b><p>El equipo buscará progresar con {game.save.tactics.tempo > 0.6 ? 'transiciones rápidas' : 'circulación controlada'}.</p></div></div>
        </aside>
      </div>

      {changesOpen && <div className="lineup-change-backdrop" onMouseDown={closeChanges}>
        <section className="lineup-change-modal card" role="dialog" aria-modal="true" aria-labelledby="lineup-change-title" onMouseDown={(event) => event.stopPropagation()}>
          <header><div><span className="eyebrow">GESTIÓN DE PLANTILLA</span><h2 id="lineup-change-title">Once, banquillo y Reserva</h2><p>Selecciona un titular o suplente, compáralo con un jugador Reserva y confirma el intercambio.</p></div><button onClick={closeChanges} aria-label="Cerrar cambios"><X size={19} /></button></header>
          <div className="lineup-change-layout">
            <section><div className="lineup-change-section-title"><span>ONCE Y BANQUILLO</span><small>{activeLineup.length + bench.length} jugadores</small></div><div className="change-roster-list">
              <div className="change-list-heading"><span>ONCE TITULAR</span><small>11 jugadores</small></div>
              {activeLineup.map((playerId, index) => { const player = ownedById.get(playerId); return player && <button className={changeTargetId === player.id ? 'active' : ''} onClick={() => { setChangeTargetId(player.id); setChangeCandidateId(null); }} key={`${playerId}-${index}`}><PlayerAvatar player={player} size="sm" /><span><small>{formation.roles[index]} · PUESTO {index + 1}</small><b>{player.name}</b></span>{!isAffinityView && game.save.captainPlayerId === player.id && <Crown size={15} />}</button>; })}
              <div className="change-list-heading bench-heading"><span>BANQUILLO</span><small>{bench.length} suplentes</small></div>
              {bench.length ? bench.map((player) => <button className={`change-bench-row ${changeTargetId === player.id ? 'active' : ''}`} onClick={() => { setChangeTargetId(player.id); setChangeCandidateId(null); }} key={player.id}><PlayerAvatar player={player} size="sm" /><span><small>{player.position} · SUPLENTE</small><b>{player.name}</b></span><ArrowLeftRight size={15} /></button>) : <div className="change-empty compact"><Users size={18} /><span><b>Banquillo vacío</b><small>Selecciona jugadores Reserva para crear alternativas.</small></span></div>}
            </div></section>
            <div className="change-comparison">
              <span className="change-profile-label is-out">SALE DE {changeTargetArea}</span>
              {changeTarget ? <CompactChangePlayerCard player={changeTarget} role={changeTargetRole} tone="out" /> : <ChangeProfilePlaceholder type="target" />}
              <div className="change-direction"><ArrowLeftRight size={22} /><span>INTERCAMBIO</span><button disabled={!changeCandidate || !changeTarget} onClick={makeLineupChange}>{changeCandidate && changeTarget ? 'Confirmar cambio' : 'Elige dos jugadores'}</button></div>
              <span className="change-profile-label is-in">ENTRA EN {changeTargetArea}</span>
              {changeCandidate ? <CompactChangePlayerCard player={changeCandidate} role={changeTargetRole} tone="in" compareWith={changeTarget} /> : <ChangeProfilePlaceholder type="candidate" />}
            </div>
            <section><div className="lineup-change-section-title"><span>JUGADORES RESERVA</span><small>{visibleCandidates.length}/{availableCandidates.length} disponibles</small></div>
              {availableCandidates.length > 0 && <div className="recruit-filter-panel"><label className="recruit-search"><Search size={15} /><input value={recruitQuery} onChange={(event) => setRecruitQuery(event.target.value)} placeholder="Buscar por nombre…" /></label><div className="recruit-filter-row"><span><SlidersHorizontal size={13} /> FILTRAR</span><FilterSelect value={recruitPosition} onChange={(value) => setRecruitPosition(value as 'ALL' | Player['position'])} ariaLabel="Filtrar jugadores por posición" options={[{ value: 'ALL', label: 'Posición' }, { value: 'GK', label: 'Portero' }, { value: 'DF', label: 'Defensa' }, { value: 'MF', label: 'Medio' }, { value: 'FW', label: 'Delantero' }]} /><FilterSelect value={recruitElement} onChange={(value) => setRecruitElement(value as 'ALL' | Player['element'])} ariaLabel="Filtrar jugadores por afinidad" options={[{ value: 'ALL', label: 'Afinidad' }, { value: 'FIRE', label: 'Fuego' }, { value: 'WIND', label: 'Viento' }, { value: 'FOREST', label: 'Bosque' }, { value: 'MOUNTAIN', label: 'Montaña' }]} /></div></div>}
              <div className="change-candidate-list">
                {availableCandidates.length === 0 ? <div className="change-empty"><Users size={21} /><span><b>Sin jugadores Reserva</b><small>Los jugadores fuera del once y banquillo aparecerán aquí.</small></span></div> : visibleCandidates.length ? visibleCandidates.map((player) => <button className={changeCandidateId === player.id ? 'active' : ''} onClick={() => setChangeCandidateId(player.id)} key={player.id}><PlayerAvatar player={player} size="sm" /><span><small>{player.position} · {player.element}</small><b>{player.name}</b></span><ArrowLeftRight size={15} /></button>) : <div className="change-empty"><Search size={21} /><span><b>Sin coincidencias</b><small>Prueba con otro nombre, posición o afinidad.</small></span></div>}
              </div>
            </section>
          </div>
          <footer><span><Check size={15} /> Los cambios se guardan automáticamente.</span><button onClick={closeChanges}>Listo</button></footer>
        </section>
      </div>}
      {profileOpen && selected && <PlayerProfileModal player={selected} techniqueScope="owned" onClose={() => setProfileOpen(false)} />}
    </div>
  );
}

function average(player: Player) {
  return playerRating(player);
}

function CompactChangePlayerCard({ player, role, tone, compareWith }: { player: Player; role: string; tone: 'in' | 'out'; compareWith?: Player }) {
  const stats = Object.entries(player.stats).slice(0, 4);
  const techniques = player.techniques;
  return <article className={`change-player-profile change-player-profile--${tone}`}>
    <div className="change-profile-identity">
      <PlayerAvatar player={player} size="lg" />
      <span><small>{role} · MEDIA {average(player)}</small><b>{player.name}</b><em>{player.metadata?.team ?? 'Jugador fichado'}</em></span>
    </div>
    <div className="change-profile-tags"><ElementBadge element={player.element} /><span>{player.position}</span><span>{player.tp_max} TP</span></div>
    <div className="change-profile-analytics">
      <div className="change-radar-frame"><StatRadar stats={player.stats} compareStats={compareWith?.stats} className="change-stat-radar" />{compareWith && <div className="change-radar-legend"><span><i className="primary" />{player.metadata?.nickname ?? player.name}</span><span><i className="comparison" />{compareWith.metadata?.nickname ?? compareWith.name}</span></div>}</div>
      <div className="change-profile-stats">{stats.map(([stat, value]) => <span key={stat}><small>{stat.toUpperCase().slice(0, 3)}</small><b>{value}</b></span>)}</div>
    </div>
    <footer><b>{techniques.length}</b> supertécnicas <span>· {player.metadata?.game ?? 'Inazuma Eleven'}</span></footer>
  </article>;
}

function ChangeProfilePlaceholder({ type }: { type: 'target' | 'candidate' }) {
  const isTarget = type === 'target';
  return <div className={`change-profile-placeholder ${isTarget ? 'is-out' : ''}`}>
    <Users size={20} />
    <b>{isTarget ? 'Cambio aplicado' : 'Selecciona un jugador Reserva'}</b>
    <small>{isTarget ? 'Elige un jugador del once o banquillo para preparar otro cambio.' : 'Su radar aparecerá aquí y se comparará con el jugador que va a salir.'}</small>
  </div>;
}

function getTacticSummary(tactics: Tactics) {
  if (tactics.risk > 0.72 && tactics.tempo > 0.62) return 'Transición vertical';
  if (tactics.defensiveLine < 0.32) return 'Bloque bajo';
  if (tactics.mentality >= 0.68) return 'Ataque agresivo';
  if (tactics.pressing > 0.7) return 'Presión dominante';
  if (tactics.tempo < 0.35) return 'Control paciente';
  return 'Equilibrio dinámico';
}

function getMentalityPreset(value: number) {
  if (value < 0.4) return mentalityPresets[0];
  if (value < 0.68) return mentalityPresets[1];
  return mentalityPresets[2];
}
